import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Heart, Send, TrendingUp, Smile, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/stores/authStore'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import { formatDistanceToNow } from '@/lib/utils'

export default function GratitudeWall() {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuthStore()
  const queryClient = useQueryClient()
  const [content, setContent] = useState('')
  const [category, setCategory] = useState('other')
  const [isPublic, setIsPublic] = useState(true)

  const categories = [
    { key: 'people', label: t('gratitude.category.people'), emoji: '👥' },
    { key: 'health', label: t('gratitude.category.health'), emoji: '💪' },
    { key: 'nature', label: t('gratitude.category.nature'), emoji: '🌿' },
    { key: 'opportunity', label: t('gratitude.category.opportunity'), emoji: '⭐' },
    { key: 'small_things', label: t('gratitude.category.smallThings'), emoji: '☕' },
    { key: 'growth', label: t('gratitude.category.growth'), emoji: '🌱' },
    { key: 'other', label: t('gratitude.category.other'), emoji: '✨' },
  ]

  const { data: stats } = useQuery({
    queryKey: ['gratitude-stats'],
    queryFn: () => api.get('/gratitude/stats').then(r => r.data)
  })

  const { data: entries } = useQuery({
    queryKey: ['gratitude-wall'],
    queryFn: () => api.get('/gratitude/wall').then(r => r.data)
  })

  const { data: myEntries } = useQuery({
    queryKey: ['my-gratitude'],
    queryFn: () => api.get('/gratitude/my').then(r => r.data),
    enabled: isAuthenticated
  })

  const createMutation = useMutation({
    mutationFn: () => api.post('/gratitude', { content, category, isPublic }),
    onSuccess: () => {
      setContent('')
      setCategory('other')
      queryClient.invalidateQueries({ queryKey: ['gratitude-wall'] })
      queryClient.invalidateQueries({ queryKey: ['my-gratitude'] })
      queryClient.invalidateQueries({ queryKey: ['gratitude-stats'] })
      toast({ title: t('gratitude.saved') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Heart className="w-7 h-7 text-primary" />
        </div>
        <h1 className="font-display text-2xl font-bold">{t('gratitude.title')}</h1>
        <p className="text-muted-foreground mt-2">{t('gratitude.subtitle')}</p>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="p-3 rounded-xl bg-card border border-border text-center">
          <TrendingUp className="w-5 h-5 text-primary mx-auto mb-1" />
          <p className="text-lg font-bold">{stats?.total || 0}</p>
          <p className="text-xs text-muted-foreground">{t('gratitude.total')}</p>
        </div>
        <div className="p-3 rounded-xl bg-card border border-border text-center">
          <Smile className="w-5 h-5 text-green-500 mx-auto mb-1" />
          <p className="text-lg font-bold">{stats?.todayCount || 0}</p>
          <p className="text-xs text-muted-foreground">{t('gratitude.today')}</p>
        </div>
        <div className="p-3 rounded-xl bg-card border border-border text-center">
          <Users className="w-5 h-5 text-blue-500 mx-auto mb-1" />
          <p className="text-lg font-bold">{myEntries?.length || 0}</p>
          <p className="text-xs text-muted-foreground">{t('gratitude.mine')}</p>
        </div>
      </div>

      {isAuthenticated && (
        <div className="mb-6 p-4 rounded-xl bg-card border border-border">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t('gratitude.placeholder')}
            maxLength={500}
            className="w-full bg-transparent resize-none focus:outline-none text-sm min-h-[60px]"
          />
          <div className="flex items-center gap-2 mt-2">
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="px-2 py-1 rounded-lg bg-secondary border border-border text-xs">
              {categories.map(c => <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>)}
            </select>
            <label className="flex items-center gap-1 text-xs text-muted-foreground">
              <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} className="rounded" />
              {t('gratitude.public')}
            </label>
            <Button size="sm" className="ml-auto" onClick={() => createMutation.mutate()} disabled={!content.trim()}>
              <Send className="w-3 h-3 mr-1" /> {t('gratitude.add')}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <h2 className="font-semibold">{t('gratitude.wallTitle')}</h2>
        {entries?.map((entry: any) => {
          const cat = categories.find(c => c.key === entry.category)
          return (
            <motion.div key={entry._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 rounded-xl bg-card border border-border">
              <div className="flex items-start gap-3">
                <span className="text-xl">{cat?.emoji || '✨'}</span>
                <div className="flex-1">
                  <p className="text-sm">{entry.content}</p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <span>{entry.username}</span>
                    <span>{formatDistanceToNow(entry.createdAt)}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )
        })}
        {(!entries || entries.length === 0) && (
          <div className="text-center py-8 text-muted-foreground text-sm">{t('gratitude.empty')}</div>
        )}
      </div>
    </div>
  )
}
